import React from "react";

// Mirrored Я as a scale-x-flipped 'R' — uses Bodoni Moda for editorial luxury feel
export function MirrorR({
    className = "",
    style = {},
    color,
    weight = 400,
    italic = false,
}) {
    return (
        <span
            aria-hidden="true"
            className={`font-didone inline-block leading-none ${className}`}
            style={{
                transform: "scaleX(-1)",
                color: color,
                fontWeight: weight,
                fontStyle: italic ? "italic" : "normal",
                ...style,
            }}
        >
            R
        </span>
    );
}

// Full wordmark INTOLLE + mirrored Я + ANTIA in a single line
export function Wordmark({
    size = "text-5xl md:text-7xl",
    color = "#0B0B0B",
    goldR = false,
    className = "",
    tracking = "tracking-[0.08em]",
}) {
    return (
        <span
            className={`font-didone font-normal ${size} ${tracking} ${className}`}
            style={{ color }}
        >
            <span>INTOLLE</span>
            <span
                aria-hidden="true"
                className="inline-block"
                style={{
                    transform: "scaleX(-1)",
                    color: goldR ? "#C9A96A" : color,
                }}
            >
                R
            </span>
            <span>ANTIA</span>
        </span>
    );
}

// Official Registered Logo Lockup — matches the reference asset exactly
export function LogoLockup({
    variant = "primary",
    payoff = "MAISON ITALIANA — EDIZIONI RARE",
    size = "large",
    className = "",
}) {
    const isDark = variant === "inverted";
    const bg = isDark ? "#0B0B0B" : "#F4EFE6";
    const fg = isDark ? "#F4EFE6" : "#0B0B0B";
    const goldR = variant === "prestige";
    const sizes = {
        small: {
            word: "text-2xl md:text-3xl",
            payoff: "text-[8px] md:text-[9px]",
            pad: "py-6 px-8",
        },
        medium: {
            word: "text-4xl md:text-6xl",
            payoff: "text-[10px] md:text-[11px]",
            pad: "py-12 px-16",
        },
        large: {
            word: "text-6xl md:text-8xl lg:text-9xl",
            payoff: "text-xs md:text-sm",
            pad: "py-20 px-16",
        },
    }[size];

    return (
        <div
            className={`w-full flex flex-col items-center justify-center ${sizes.pad} ${className}`}
            style={{ background: bg }}
        >
            <Wordmark size={sizes.word} color={fg} goldR={goldR} />
            {payoff !== null && (
                <>
                    <div
                        className="mt-6 md:mt-10 h-px w-24"
                        style={{ background: "#C9A96A" }}
                    />
                    <div
                        className={`mt-5 md:mt-8 font-micro ${sizes.payoff}`}
                        style={{
                            color: fg,
                            letterSpacing: "0.4em",
                            opacity: 0.85,
                        }}
                    >
                        {payoff}
                    </div>
                </>
            )}
        </div>
    );
}

// Circular monogram seal with curved micro-type
export function MonogramSeal({
    size = 220,
    color = "#0B0B0B",
    ring = "#C9A96A",
    text = "INTOLLERANTIA · MAISON ITALIANA · EDIZIONI RARE ·",
}) {
    const r = size / 2 - 12;
    const cx = size / 2;
    const cy = size / 2;
    return (
        <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            className="block"
            aria-hidden="true"
        >
            <defs>
                <path
                    id={`seal-path-${size}`}
                    d={`M ${cx},${cy} m -${r},0 a ${r},${r} 0 1,1 ${r * 2},0 a ${r},${r} 0 1,1 -${r * 2},0`}
                    fill="none"
                />
            </defs>
            <circle
                cx={cx}
                cy={cy}
                r={r}
                fill="none"
                stroke={ring}
                strokeWidth="1"
            />
            <circle
                cx={cx}
                cy={cy}
                r={r - 10}
                fill="none"
                stroke={ring}
                strokeWidth="0.5"
                opacity="0.5"
            />
            <text
                fill={color}
                fontFamily="Inter, sans-serif"
                fontSize={size * 0.045}
                letterSpacing="4"
                textLength={2 * Math.PI * r * 0.98}
            >
                <textPath xlinkHref={`#seal-path-${size}`} startOffset="0">
                    {text}
                </textPath>
            </text>
            <text
                x={cx}
                y={cy + size * 0.11}
                textAnchor="middle"
                fill={color}
                fontFamily="Bodoni Moda, Didot, serif"
                fontSize={size * 0.42}
                transform={`scale(-1,1) translate(${-2 * cx},0)`}
            >
                R
            </text>
        </svg>
    );
}
